import React, { useState, useEffect, useContext } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { AuthContext } from '../App';
import { formatCurrency } from '../utils/formatUtils';
import { 
  Plus, 
  Users, 
  TrendingUp, 
  DollarSign,
  Edit3,
  Trash2,
  Eye,
  Key,
  Globe,
  AlertCircle,
  Calendar,
  BarChart3
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AffiliatesPage = () => {
  const { user } = useContext(AuthContext);
  const [affiliates, setAffiliates] = useState([]);
  const { tt } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [affiliateStats, setAffiliateStats] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_person: '',
    description: '',
    webhook_url: ''
  });

  // Проверка прав доступа
  if (user?.role !== 'superadmin' && user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{tt('Доступ запрещен')}</h2>
          <p className="text-gray-600 mb-6">{tt('Только администратор может управлять аффилейтами')}</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    fetchAffiliates();
  }, []);

  const fetchAffiliates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/affiliates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAffiliates(response.data);
    } catch (error) {
      console.error('Error fetching affiliates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAffiliate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/affiliates/${selectedAffiliate.id}`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setShowEditModal(false);
      setSelectedAffiliate(null);
      setFormData({
        name: '',
        contact_email: '',
        contact_person: '',
        description: '',
        webhook_url: ''
      });
      fetchAffiliates();
    } catch (error) {
      console.error('Error updating affiliate:', error);
      alert(error.response?.data?.detail || 'Ошибка при обновлении аффилейта');
    }
  };

  const handleDeleteAffiliate = async (affiliate) => {
    if (window.confirm(`Вы уверены, что хотите удалить аффилейта "${affiliate.name}"?`)) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/affiliates/${affiliate.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        fetchAffiliates();
      } catch (error) {
        console.error('Error deleting affiliate:', error);
        alert(error.response?.data?.detail || 'Ошибка при удалении аффилейта');
      }
    }
  };

  const openEditModal = (affiliate) => {
    setSelectedAffiliate(affiliate);
    setFormData({
      name: affiliate.name,
      contact_email: affiliate.contact_email || '',
      contact_person: affiliate.contact_person || '',
      description: affiliate.description || '',
      webhook_url: affiliate.webhook_url || ''
    });
    setShowEditModal(true);
  };

  const fetchAffiliateStats = async (affiliate) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/affiliates/${affiliate.id}/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAffiliateStats(response.data);
      setSelectedAffiliate(affiliate);
      setShowStatsModal(true);
    } catch (error) {
      console.error('Error fetching affiliate stats:', error);
    }
  };

  const copyApiToken = (token) => {
    navigator.clipboard.writeText(token);
    alert('API токен скопирован в буфер обмена');
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      paused: 'bg-yellow-100 text-yellow-800'
    };
    const labels = {
      active: tt('Активен'),
      inactive: tt('Неактивен'),
      paused: tt('Приостановлен')
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || colors.inactive}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="page-container">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{tt('Аффилейты')}</h1>
          <p className="page-subtitle">{tt('Управление поставщиками лидов (добавляются через интеграцию)')}</p>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <div className="text-2xl font-bold text-blue-600">{affiliates.length}</div>
          <div className="text-sm text-gray-600">{tt('Всего аффилейтов')}</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-green-600">
            {affiliates.filter(a => a.status === 'active').length}
          </div>
          <div className="text-sm text-gray-600">{tt('Активных')}</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-yellow-600">
            {affiliates.filter(a => a.status === 'paused').length}
          </div>
          <div className="text-sm text-gray-600">{tt('Приостановлено')}</div>
        </div>
        <div className="card p-4">
          <div className="text-2xl font-bold text-gray-600">
            {affiliates.reduce((sum, a) => sum + (a.total_clients || 0), 0)}
          </div>
          <div className="text-sm text-gray-600">{tt('Общее клиентов')}</div>
        </div>
      </div>

      {/* Affiliates Table */}
      <div className="content-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="spinner mr-3"></div>
            <span className="text-gray-600">{tt('Загрузка аффилейтов...')}</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table compact-table">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-xs font-medium">{tt('Название')}</th>
                  <th className="px-3 py-2 text-xs font-medium">{tt('Контактное лицо')}</th>
                  <th className="px-3 py-2 text-xs font-medium">Email</th>
                  <th className="px-3 py-2 text-xs font-medium">{tt('Статус')}</th>
                  <th className="px-3 py-2 text-xs font-medium">{tt('Клиенты')}</th>
                  <th className="px-3 py-2 text-xs font-medium">{tt('Кампании')}</th>
                  <th className="px-3 py-2 text-xs font-medium">{tt('API токен')}</th>
                  <th className="px-3 py-2 text-xs font-medium">{tt('Действия')}</th>
                </tr>
              </thead>
              <tbody>
                {affiliates.map((affiliate) => (
                  <tr key={affiliate.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs font-medium text-blue-600">
                      {affiliate.name}
                    </td>
                    <td className="px-3 py-2 text-xs">{affiliate.contact_person || '-'}</td>
                    <td className="px-3 py-2 text-xs">{affiliate.contact_email || '-'}</td>
                    <td className="px-3 py-2 text-xs">{getStatusBadge(affiliate.status)}</td>
                    <td className="px-3 py-2 text-xs">{affiliate.total_clients || 0}</td>
                    <td className="px-3 py-2 text-xs">{affiliate.campaigns?.length || 0}</td>
                    <td className="px-3 py-2 text-xs">
                      <div className="flex items-center space-x-2">
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {affiliate.api_token?.substring(0, 8)}...
                        </code>
                        <button
                          onClick={() => copyApiToken(affiliate.api_token)}
                          className="text-gray-400 hover:text-gray-600"
                          title={tt('Скопировать API токен')}
                        >
                          <Key className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="flex space-x-1">
                        <button
                          onClick={() => fetchAffiliateStats(affiliate)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title={tt('Статистика')}
                        >
                          <BarChart3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(affiliate)}
                          className="text-gray-600 hover:text-gray-800 p-1"
                          title={tt('Редактировать')}
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteAffiliate(affiliate)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title={tt('Удалить')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {affiliates.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{tt('Нет аффилейтов')}</h3>
                <p className="text-gray-500 mb-4">{tt('Аффилейты добавляются через интеграцию')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Affiliate Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">{tt('Редактировать аффилейта')}</h2>
            <form onSubmit={handleEditAffiliate}>
              <div className="space-y-4">
                <div>
                  <label className="form-label">{tt('Название *')}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">{tt('Контактное лицо')}</label>
                  <input
                    type="text"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">Webhook URL</label>
                  <input
                    type="url"
                    value={formData.webhook_url}
                    onChange={(e) => setFormData({...formData, webhook_url: e.target.value})}
                    className="form-input"
                    placeholder="https://example.com/webhook"
                  />
                </div>
                <div>
                  <label className="form-label">{tt('Описание')}</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="form-input"
                    rows="3"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="btn btn-secondary"
                >
                  {tt('Отмена')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {tt('Обновить')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {showStatsModal && affiliateStats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <h2 className="text-xl font-bold mb-4">
              Статистика: {affiliateStats.affiliate_name}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{affiliateStats.total_clients}</div>
                <div className="text-sm text-blue-800">{tt('Всего клиентов')}</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{affiliateStats.ftd_clients}</div>
                <div className="text-sm text-green-800">{tt('С депозитами (FTD)')}</div>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{affiliateStats.conversion_rate}%</div>
                <div className="text-sm text-yellow-800">{tt('Конверсия в FTD')}</div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{affiliateStats.total_campaigns}</div>
                <div className="text-sm text-purple-800">{tt('Кампаний')}</div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <h4 className="font-medium text-gray-900 mb-2">{tt('Общая сумма депозитов')}</h4>
              <div className="text-2xl font-bold text-gray-900">{formatCurrency(affiliateStats.total_deposits, true)}</div>
              <div className="text-sm text-gray-600">
                Средний депозит: {formatCurrency(affiliateStats.avg_deposit, true)}
              </div>
            </div>

            {affiliateStats.campaigns.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-2">{tt('Кампании:')}</h4>
                <div className="flex flex-wrap gap-2">
                  {affiliateStats.campaigns.map((campaign, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                      {campaign}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => setShowStatsModal(false)}
                className="btn btn-primary"
              >
                {tt('Закрыть')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliatesPage;